import { useCallback, useMemo, useRef } from 'react';
import { useColorModeValue } from '@chakra-ui/react';
import { format, formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import {
  AxisLabelsFormatterContextObject,
  Chart,
  GradientColorObject,
  Options,
  Point,
  SeriesOptionsType,
  SVGElement,
  TooltipFormatterContextObject,
} from 'highcharts';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DateRangeType } from '../../../common/components/DateRangeTypePicker';
import { useRegionalConfig } from '../../../common/hooks/use-regional-config';
import { CHART_DEFAULT_OPTIONS, SERIES_DEFAULT_OPTIONS } from '../../../features/activity/constants';
import { getMaxDateTime, getMinDateTime, getUIConfigForRangeType } from '../../../features/activity/utils';
import { selectSite } from '../../../features/site/siteSlice';
import {
  formatDynamicDecimal,
  getChartTimezoneOffset,
  getLocaleForDateFns,
  SupportedLocales,
} from '../../../utils/common/common-utils';
import { ChargingActivityData, ChargingActivitySessionData } from '../types';

type ChargingHistorySeries = SeriesOptionsType;

type SeriesPoint = number | [string | number, number | null] | { y?: number | null } | null;

function getSeriesPointCount(series: ChargingHistorySeries[]) {
  const firstSeries = series[0];
  if (!firstSeries || !('data' in firstSeries) || !Array.isArray(firstSeries.data)) return 0;

  return firstSeries.data.length;
}

function getMonthCategories(count: number, weekLabel: string) {
  return Array.from({ length: count }, (_, index) => `${weekLabel} ${index + 1}`);
}

type HoverMarkerChart = Chart & { hoverPointMarker?: SVGElement };

type PinchState = {
  distance: number;
  range: number;
  center: number;
  centerRatio: number;
};

type PanState = {
  startX: number;
  startY: number;
  min: number;
  max: number;
  isPanning: boolean;
};

const DAY_MIN_ZOOM_RANGE_MS = 30 * 60 * 1000;
const PAN_ACTIVATION_DISTANCE_PX = 8;
const DAY_TICK_INTERVALS_MS = [5, 10, 15, 30, 60, 120, 180, 360, 720].map((minutes) => minutes * 60 * 1000);
const DAY_TARGET_TICK_COUNT = 4;
const NICE_AXIS_MAX_STEPS = [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10];

function getDayTickInterval(range: number) {
  return (
    DAY_TICK_INTERVALS_MS.find((interval) => range / interval <= DAY_TARGET_TICK_COUNT) ??
    DAY_TICK_INTERVALS_MS[DAY_TICK_INTERVALS_MS.length - 1]
  );
}

/** Ticks anchored to the start of the day so they stay aligned to local time at every zoom level. */
function getDayTickPositions(min: number, max: number, anchor: number) {
  const interval = getDayTickInterval(max - min);
  const positions: number[] = [];

  for (let tick = anchor + Math.ceil((min - anchor) / interval) * interval; tick <= max; tick += interval) {
    positions.push(tick);
  }

  return positions;
}

/** Rounds up to a readable axis maximum so the grid stays identical regardless of the visible data. */
function getNiceAxisMax(value: number) {
  if (value <= 0) return 1;

  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const step = NICE_AXIS_MAX_STEPS.find((candidate) => normalized <= candidate) ?? 10;

  return step * magnitude;
}

function renderHoverPointMarker(point: Point, color: string) {
  const chart = point.series.chart as HoverMarkerChart;
  if (point.plotX == null || point.plotY == null) return;

  const x = chart.plotLeft + point.plotX;
  const y = chart.plotTop + point.plotY;

  if (!chart.hoverPointMarker) {
    chart.hoverPointMarker = chart.renderer
      .circle(x, y, 6)
      .attr({
        fill: color,
        stroke: '#FFFFFF',
        'stroke-width': 2,
        zIndex: 8,
      })
      .css({ pointerEvents: 'none' })
      .add();
    return;
  }

  chart.hoverPointMarker.attr({ cx: x, cy: y });
}

function clearHoverPointMarker(chart: Chart) {
  const hoverMarkerChart = chart as HoverMarkerChart;
  hoverMarkerChart.hoverPointMarker?.destroy();
  hoverMarkerChart.hoverPointMarker = undefined;
}

interface Props {
  series: ChargingHistorySeries[];
  selectedDateRangeType: DateRangeType;
  rangeTypeToSelectedPeriod: Record<DateRangeType, Date>;
  chartOpts: {
    height: number; // in pixels
  };
  isCostViewSelected: boolean;
  onDrillDown?: (date: Date) => void;
  onZoomChange?: (state: { isZoomed: boolean }) => void;
}

export function useChargingHistoryChartData({
  series,
  selectedDateRangeType,
  rangeTypeToSelectedPeriod,
  chartOpts,
  isCostViewSelected,
  onDrillDown,
  onZoomChange,
}: Props) {
  const onDrillDownRef = useRef(onDrillDown);
  onDrillDownRef.current = onDrillDown;
  const onZoomChangeRef = useRef(onZoomChange);
  onZoomChangeRef.current = onZoomChange;
  const markerColor = useColorModeValue('#3A3A3C', '#BCBCBC');
  const tooltipValueColor = useColorModeValue('#3A3A3C', '#FFFFFF');
  const crosshairColor = '#CCCCCC';
  const tooltipCardBg = useColorModeValue('rgba(255,255,255,0.92)', 'rgba(30,30,30,0.92)');
  const tooltipLabelColor = useColorModeValue('#888888', '#AAAAAA');
  const seriesColor = useColorModeValue('#0087CD', '#42B4E6');
  const fillColorStart = useColorModeValue('rgba(0, 135, 205, 0.6)', 'rgba(66, 180, 230, 0.45)');
  const fillColorEnd = useColorModeValue('rgba(0, 135, 205, 0)', 'rgba(66, 180, 230, 0)');
  const { timezone } = useSelector(selectSite);
  const { i18n, t } = useTranslation();
  const { weekStartsOn, hour12 } = useRegionalConfig();

  const effectiveHour12 = useMemo(() => {
    const persistedTimeFormat = localStorage.getItem('timeFormat');
    if (persistedTimeFormat === '24HR') return false;
    if (persistedTimeFormat === 'AM/PM') return true;
    return hour12;
  }, [hour12]);

  return useMemo<Options>(() => {
    const visibleValues = series.flatMap((chartSeries) => {
      return (
        ('data' in chartSeries ? (chartSeries.data as SeriesPoint[] | undefined) : [])
          ?.map((point: SeriesPoint) => {
            if (typeof point === 'number') return point;
            if (Array.isArray(point)) return point[1];
            return point?.y;
          })
          .filter((value: number | null | undefined): value is number => typeof value === 'number') ?? []
      );
    });
    const maxVisibleValue = visibleValues.reduce((max, value) => (value > max ? value : max), 0);
    const isDayView = selectedDateRangeType === DateRangeType.Day;
    const isMonthView = selectedDateRangeType === DateRangeType.Month;
    const isWeekView = selectedDateRangeType === DateRangeType.Week;
    const { height } = chartOpts;
    const localeForDateFns = getLocaleForDateFns(i18n.language as SupportedLocales);
    const startDate = rangeTypeToSelectedPeriod[selectedDateRangeType];
    const { xAxisTickFormat, xAxisTickInterval } = getUIConfigForRangeType(selectedDateRangeType, effectiveHour12);
    const monthPointCount = isMonthView ? getSeriesPointCount(series) : 0;
    const monthCategories = isMonthView ? getMonthCategories(monthPointCount, t('Common.week')) : undefined;
    let sufix = isCostViewSelected ? '' : ' kWh';
    sufix = isDayView ? ' kW' : sufix;
    const prefix = isCostViewSelected && !isDayView ? '$' : '';
    const columnGradient: GradientColorObject = {
      linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
      stops: [[0, fillColorStart] as [number, string], [1, fillColorEnd] as [number, string]],
    };
    const xAxisMin = isMonthView ? undefined : getMinDateTime(startDate, selectedDateRangeType, timezone, weekStartsOn);
    const xAxisMax = isMonthView
      ? undefined
      : getMaxDateTime(startDate, selectedDateRangeType, timezone, weekStartsOn) +
        (!isDayView && !isWeekView ? xAxisTickInterval / 2 : 0);
    let tooltipTimestampFormatter: Intl.DateTimeFormat;

    if (selectedDateRangeType === DateRangeType.Day) {
      tooltipTimestampFormatter = new Intl.DateTimeFormat(i18n.language, {
        timeZone: timezone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: effectiveHour12,
      });
    } else if (selectedDateRangeType === DateRangeType.Year) {
      tooltipTimestampFormatter = new Intl.DateTimeFormat(i18n.language, {
        timeZone: timezone,
        month: 'long',
      });
    } else if (isMonthView) {
      tooltipTimestampFormatter = new Intl.DateTimeFormat(i18n.language, {
        timeZone: timezone,
        month: 'short',
        day: 'numeric',
      });
    } else {
      tooltipTimestampFormatter = new Intl.DateTimeFormat(i18n.language, {
        timeZone: timezone,
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }

    return {
      ...CHART_DEFAULT_OPTIONS,
      chart: {
        ...CHART_DEFAULT_OPTIONS.chart,
        height: `${height}px`,
        animation: isDayView ? false : undefined,
        events: !isDayView
          ? {
              render: function () {
                // eslint-disable-next-line @typescript-eslint/no-this-alias
                const chart = this;
                const axis = chart.xAxis[0];
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                Object.values(axis.ticks).forEach((tick: any) => {
                  if (!tick.label?.element) return;
                  const el = tick.label.element as HTMLElement;
                  el.style.cursor = 'pointer';
                  const handler = () => {
                    let drillDate: Date | null = null;
                    if (isMonthView) {
                      const series0 = chart.series[0];
                      const point = series0?.data?.[tick.pos];
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      const ss = (point as any)?.options?.custom?.sessionStart;
                      if (ss) {
                        const p = String(ss).substring(0, 10).split('-');
                        drillDate = new Date(+p[0], +p[1] - 1, +p[2]);
                      }
                    } else {
                      const dateStr = formatInTimeZone(new Date(tick.pos), timezone, 'yyyy-MM-dd');
                      const p = dateStr.split('-');
                      drillDate = new Date(+p[0], +p[1] - 1, +p[2]);
                    }
                    if (drillDate && onDrillDownRef.current) {
                      onDrillDownRef.current(drillDate);
                    }
                  };
                  // Avoid duplicate listeners on re-render
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const elAny = el as any;
                  if (elAny._drillHandler) el.removeEventListener('click', elAny._drillHandler);
                  elAny._drillHandler = handler;
                  el.addEventListener('click', handler);
                });
              },
            }
          : {
              load: function () {
                const container = this.container;
                const axis = this.xAxis[0];
                if (!container || xAxisMin == null || xAxisMax == null) return;

                let pinchState: PinchState | undefined;
                let panState: PanState | undefined;
                let pendingExtremes: { min: number; max: number } | undefined;
                let redrawFrame: number | undefined;

                // Read from the axis options rather than the closure: this handler only runs once, but the
                // selected day (and therefore the allowed range) changes on every chart update.
                const getDayBounds = () => ({
                  min: axis.options.min ?? xAxisMin,
                  max: axis.options.max ?? xAxisMax,
                });

                const clampToDayBounds = (min: number, max: number) => {
                  const bounds = getDayBounds();
                  const range = Math.min(max - min, bounds.max - bounds.min);

                  if (min < bounds.min) return { min: bounds.min, max: bounds.min + range };
                  if (min + range > bounds.max) return { min: bounds.max - range, max: bounds.max };
                  return { min, max: min + range };
                };

                const scheduleExtremes = (min: number, max: number) => {
                  pendingExtremes = { min, max };
                  if (redrawFrame != null) return;

                  redrawFrame = requestAnimationFrame(() => {
                    redrawFrame = undefined;
                    if (!pendingExtremes) return;
                    axis.setExtremes(pendingExtremes.min, pendingExtremes.max, true, false, { trigger: 'pinch' });
                    pendingExtremes = undefined;
                  });
                };

                const getVisibleExtremes = () => {
                  const bounds = getDayBounds();
                  return {
                    min: Math.max(axis.min ?? bounds.min, bounds.min),
                    max: Math.min(axis.max ?? bounds.max, bounds.max),
                    bounds,
                  };
                };

                const getTouchGeometry = (touches: TouchList) => {
                  const firstTouch = touches.item(0);
                  const secondTouch = touches.item(1);
                  if (!firstTouch || !secondTouch) return;

                  const bounds = container.getBoundingClientRect();
                  return {
                    // 2D distance so vertical and diagonal pinches also scale the x-axis
                    distance: Math.hypot(
                      firstTouch.clientX - secondTouch.clientX,
                      firstTouch.clientY - secondTouch.clientY
                    ),
                    midpoint: (firstTouch.clientX + secondTouch.clientX) / 2 - bounds.left,
                  };
                };

                const onStart = (event: TouchEvent) => {
                  if (event.touches.length < 2) {
                    const touch = event.touches.item(0);
                    const { min, max, bounds } = getVisibleExtremes();
                    // Only a zoomed-in axis can pan; otherwise leave the touch to Highcharts' tooltip handling
                    if (!touch || max - min >= bounds.max - bounds.min) return;

                    panState = { startX: touch.clientX, startY: touch.clientY, min, max, isPanning: false };
                    return;
                  }

                  const geometry = getTouchGeometry(event.touches);
                  if (!geometry || geometry.distance === 0) return;

                  event.preventDefault();
                  event.stopImmediatePropagation();
                  panState = undefined;
                  const { min, max } = getVisibleExtremes();
                  const range = max - min;
                  const center = Math.min(Math.max(axis.toValue(geometry.midpoint), min), max);
                  pinchState = {
                    distance: geometry.distance,
                    range,
                    center,
                    centerRatio: (center - min) / range,
                  };
                  clearHoverPointMarker(this);
                  container.classList.add('hc-pinching');
                };

                const onMove = (event: TouchEvent) => {
                  if (panState && event.touches.length < 2) {
                    const touch = event.touches.item(0);
                    if (!touch) return;

                    const deltaX = touch.clientX - panState.startX;
                    const deltaY = touch.clientY - panState.startY;

                    if (!panState.isPanning) {
                      if (Math.abs(deltaX) < PAN_ACTIVATION_DISTANCE_PX) {
                        // A mostly vertical drag belongs to the page scroll, not the chart
                        if (Math.abs(deltaY) >= PAN_ACTIVATION_DISTANCE_PX) panState = undefined;
                        return;
                      }
                      if (Math.abs(deltaX) <= Math.abs(deltaY)) {
                        panState = undefined;
                        return;
                      }

                      panState.isPanning = true;
                      clearHoverPointMarker(this);
                      this.pointer?.reset(false, 0);
                      container.classList.add('hc-pinching');
                    }

                    event.preventDefault();
                    event.stopImmediatePropagation();
                    const range = panState.max - panState.min;
                    const valueDelta = (deltaX / this.plotWidth) * range;
                    const { min, max } = clampToDayBounds(panState.min - valueDelta, panState.max - valueDelta);
                    scheduleExtremes(min, max);
                    return;
                  }

                  if (!pinchState || event.touches.length < 2) return;
                  const geometry = getTouchGeometry(event.touches);
                  if (!geometry || geometry.distance === 0) return;

                  event.preventDefault();
                  event.stopImmediatePropagation();
                  const bounds = getDayBounds();
                  const nextRange = Math.min(
                    bounds.max - bounds.min,
                    Math.max(DAY_MIN_ZOOM_RANGE_MS, pinchState.range * (pinchState.distance / geometry.distance))
                  );
                  // Anchored on the value under the initial finger midpoint, so the zoom never drifts
                  const { min, max } = clampToDayBounds(
                    pinchState.center - pinchState.centerRatio * nextRange,
                    pinchState.center + (1 - pinchState.centerRatio) * nextRange
                  );
                  scheduleExtremes(min, max);
                };

                const onEnd = (event: TouchEvent) => {
                  if (panState && event.touches.length === 0) {
                    const wasPanning = panState.isPanning;
                    panState = undefined;
                    if (wasPanning) {
                      // Swallow the event so the pan doesn't end up selecting a point
                      event.preventDefault();
                      event.stopImmediatePropagation();
                      container.classList.remove('hc-pinching');
                    }
                  }

                  if (!pinchState || event.touches.length >= 2) return;

                  event.preventDefault();
                  event.stopImmediatePropagation();
                  pinchState = undefined;
                  container.classList.remove('hc-pinching');
                };

                container.addEventListener('touchstart', onStart, { capture: true, passive: false });
                container.addEventListener('touchmove', onMove, { capture: true, passive: false });
                container.addEventListener('touchend', onEnd, { capture: true, passive: false });
                container.addEventListener('touchcancel', onEnd, { capture: true, passive: false });
              },
            },
      },
      xAxis: {
        ...CHART_DEFAULT_OPTIONS.xAxis,
        type: isMonthView ? 'category' : 'datetime',
        min: xAxisMin,
        max: xAxisMax,
        ...(isWeekView ? { maxPadding: 0.04 } : {}),
        categories: monthCategories,
        accessibility: { enabled: true, description: t('Activity.time of the day') },
        tickInterval: isMonthView ? 1 : isDayView ? undefined : xAxisTickInterval,
        tickPositioner: isDayView
          ? function () {
              const min = this.min ?? xAxisMin ?? 0;
              const max = this.max ?? xAxisMax ?? 0;

              return getDayTickPositions(min, max, xAxisMin ?? min);
            }
          : undefined,
        tickmarkPlacement: isMonthView ? 'on' : undefined,
        labels: {
          style: {
            fontSize: isMonthView ? '10px' : '11px',
            color: markerColor,
            ...(!isDayView ? { cursor: 'pointer' } : {}),
          },
          formatter: function (this: AxisLabelsFormatterContextObject) {
            if (isDayView) {
              const visibleRange = (this.axis.max ?? 0) - (this.axis.min ?? 0);
              const showsMinutes = getDayTickInterval(visibleRange) < 60 * 60 * 1000;
              const tickFormat = showsMinutes && effectiveHour12 ? 'h:mm aaa' : xAxisTickFormat;
              return formatInTimeZone(new Date(this.value ?? 0), timezone, tickFormat);
            } else if (isMonthView) {
              return String(this.axis.categories?.[this.pos] ?? this.value ?? '');
            } else {
              return format(new Date(this.value ?? 0), xAxisTickFormat, { locale: localeForDateFns });
            }
          },
        },
        crosshair: {
          width: 1,
          color: crosshairColor,
        },
        events: isDayView
          ? {
              afterSetExtremes: function (event) {
                const isZoomed =
                  (xAxisMin != null && event.min != null && event.min > xAxisMin) ||
                  (xAxisMax != null && event.max != null && event.max < xAxisMax);
                onZoomChangeRef.current?.({ isZoomed });
              },
            }
          : undefined,
      },
      yAxis: {
        gridLineDashStyle: 'ShortDash',
        min: 0,
        // Day view is zoomable, so its scale is pinned to the full dataset to keep the grid identical
        // no matter whether the zoomed window is empty or flat at zero.
        max: isDayView ? getNiceAxisMax(maxVisibleValue) : maxVisibleValue > 0 ? undefined : 1,
        title: {
          text: '',
        },
        accessibility: { enabled: true, description: t('Activity.usage in kw') },
        labels: {
          style: { fontSize: '11px', color: markerColor },
          formatter: function (data) {
            return `${prefix}${data.value}${sufix}`;
          },
        },
      },
      tooltip: {
        ...CHART_DEFAULT_OPTIONS.tooltip,
        useHTML: true,
        outside: true,
        backgroundColor: 'none',
        borderWidth: 0,
        shadow: false,
        padding: 0,
        borderRadius: 0,
        style: { zIndex: 100, pointerEvents: 'none' },
        positioner: function (_labelWidth, _labelHeight, point) {
          const chart = this.chart;
          const x = Math.min(
            Math.max(chart.plotLeft, (point.plotX ?? 0) + chart.plotLeft - _labelWidth / 2),
            chart.plotLeft + chart.plotWidth - _labelWidth
          );
          return { x, y: chart.plotTop - _labelHeight - 8 };
        },
        formatter: function () {
          function format(context: TooltipFormatterContextObject) {
            const point1 = context.points?.find(({ y }) => typeof y === 'number' && y !== 0);

            if (!point1 || typeof point1.y !== 'number') return false;
            const formattedValue = formatDynamicDecimal(point1.y);
            const tooltipCategory = 'category' in point1 ? String(point1.category ?? '') : '';
            const tooltipX = typeof context.x === 'number' ? context.x : null;
            let tooltipLabel = '';

            if (isMonthView) {
              tooltipLabel = tooltipCategory;
            } else if (tooltipX !== null) {
              tooltipLabel = tooltipTimestampFormatter.format(new Date(tooltipX));
            }
            return `<div style="background:${tooltipCardBg};border-radius:10px;padding:10px 14px;box-shadow:0 2px 8px rgba(0,0,0,0.15);">${
              tooltipLabel
                ? `<div style="font-size:12px;color:${tooltipLabelColor};margin-bottom:4px;">${tooltipLabel}</div>`
                : ''
            }<div style="font-size:18px;font-weight:600;color:${tooltipValueColor};">${prefix}${formattedValue}${sufix}</div></div>`;
          }

          return format(this);
        },
      },
      plotOptions: {
        series: {
          point: {
            events: {
              mouseOver: function () {
                renderHoverPointMarker(this, seriesColor);
              },
              mouseOut: function () {
                clearHoverPointMarker(this.series.chart);
              },
            },
          },
        },
      },
      series: structuredClone(
        series.map((seriesItem): ChargingHistorySeries => {
          const baseSeries = {
            id: 'id' in seriesItem ? seriesItem.id : undefined,
            name: 'name' in seriesItem ? seriesItem.name : undefined,
            data: 'data' in seriesItem ? seriesItem.data : undefined,
            // Prevent Highcharts from dropping object-based points when dataset size is large.
            turboThreshold: 0,
            marker: {
              enabled: false,
              symbol: 'circle',
              states: {
                hover: {
                  enabled: false,
                },
              },
            },
          };

          if (isDayView) {
            return {
              ...baseSeries,
              type: 'area',
              color: seriesColor,
              fillColor: {
                linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                stops: [
                  [0, fillColorStart],
                  [1, fillColorEnd],
                ],
              },
              lineWidth: 2,
              connectNulls: false,
            } as ChargingHistorySeries;
          }

          return {
            ...baseSeries,
            type: 'column',
            color: columnGradient,
            borderColor: seriesColor,
            borderWidth: 1,
            groupPadding: 0.08,
            pointPadding: 0.08,
            ...(isMonthView ? {} : { pointRange: xAxisTickInterval }),
          } as ChargingHistorySeries;
        })
      ),
      time: {
        getTimezoneOffset: (timestamp: number) => getChartTimezoneOffset(timestamp, timezone),
      },
    };
  }, [
    selectedDateRangeType,
    chartOpts,
    isCostViewSelected,
    rangeTypeToSelectedPeriod,
    i18n.language,
    series,
    timezone,
    t,
    weekStartsOn,
    effectiveHour12,
    markerColor,
    tooltipValueColor,
    crosshairColor,
    tooltipCardBg,
    tooltipLabelColor,
    seriesColor,
    fillColorStart,
    fillColorEnd,
  ]);
}

export function useChargingHistorySeries(
  data: ChargingActivityData,
  selectedDateRangeType: DateRangeType,
  isCostViewSelected: boolean
) {
  const { timezone } = useSelector(selectSite);
  const generateChartData = useCallback(
    (sessions: ChargingActivitySessionData[], selectedDateRangeType: DateRangeType) => {
      const isDayView = selectedDateRangeType === DateRangeType.Day;
      const isMonthView = selectedDateRangeType === DateRangeType.Month;

      const points = sessions.flatMap((current) => {
        const start = fromZonedTime(current.start, timezone).getTime();
        const end = fromZonedTime(current.end, timezone).getTime();
        const usage = isCostViewSelected && !isDayView ? current.cost : current.usage;

        if (isDayView) {
          return [
            { x: start, y: usage, custom: { sessionStart: current.start } },
            { x: end, y: usage, custom: { sessionStart: current.start } },
          ];
        }

        if (isMonthView) {
          return {
            y: usage,
            custom: { sessionStart: current.start },
          };
        }

        return {
          x: start,
          y: usage,
          custom: { sessionStart: current.start },
        };
      });

      // Sessions aren't guaranteed to arrive in chronological order; Highcharts needs ascending x values
      // to hit-test the correct point on hover/tap, otherwise the marker can land on the wrong point.
      if (isMonthView) return points;
      return [...points].sort((a, b) => ('x' in a ? a.x ?? 0 : 0) - ('x' in b ? b.x ?? 0 : 0));
    },
    [isCostViewSelected, timezone]
  );

  const chartData = useMemo(
    () => generateChartData(data.chargingSessions, selectedDateRangeType),
    [data.chargingSessions, generateChartData, selectedDateRangeType]
  );
  const isDayView = selectedDateRangeType === DateRangeType.Day;

  const energyConsumedSeries = useMemo((): ChargingHistorySeries[] => {
    if (isDayView) {
      return [
        {
          ...SERIES_DEFAULT_OPTIONS,
          id: 'charging-history-chart',
          type: 'area',
          data: chartData,
          connectNulls: false,
        } as ChargingHistorySeries,
      ];
    }

    return [
      {
        id: 'charging-history-chart',
        type: 'column',
        data: chartData,
      } as ChargingHistorySeries,
    ];
  }, [chartData, isDayView]);

  return { energyConsumedSeries };
}
